<template>
  <v-container>
    <!-- Add New User Button -->
    <v-row class="align-center">
      <v-col cols="12">
        <div style="display: flex !important; justify-content: space-between; flex-wrap: wrap">
          <div class="text-h5 font-weight-medium mb-10">User Management</div>
          <div class="d-flex ga-3 text-copy">
            <v-btn
              v-if="userRole === 'client_admin'"
              data-testid="add-user-btn"
              class="text-grey-darken-2"
              variant="text"
              size="small"
              prepend-icon="mdi-plus-box"
              @click="navigateToCreateUser"
            >
              Add New User
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Users Table -->
    <v-card class="mb-4">
      <v-toolbar color="primary" density="comfortable" flat>
        <!-- Filter Menu Button -->
        <v-menu offset-y v-model="filterMenuOpen" :close-on-content-click="false">
          <template v-slot:activator="{ props }">
            <v-btn
              data-testid="filter-menu-btn"
              v-bind="props"
              variant="text"
              class="text-white ml-2"
              prepend-icon="mdi-filter-variant"
            >
              Filter Roles
              <v-badge
                v-if="selectedRoles.length < 3"
                :content="selectedRoles.length"
                color="error"
                inline
                class="ml-2"
              />
            </v-btn>
          </template>
          <v-list density="compact">
            <v-list-item>
              <v-checkbox
                data-testid="filter-user"
                v-model="tempSelectedRoles"
                value="user"
                density="compact"
                hide-details
                :label="`User (${roleCounts.user})`"
              />
            </v-list-item>
            <v-list-item>
              <v-checkbox
                data-testid="filter-manager"
                v-model="tempSelectedRoles"
                value="manager"
                density="compact"
                hide-details
                :label="`Manager (${roleCounts.manager})`"
              />
            </v-list-item>
            <v-list-item>
              <v-checkbox
                data-testid="filter-admin"
                v-model="tempSelectedRoles"
                value="admin"
                density="compact"
                hide-details
                :label="`Admin (${roleCounts.admin})`"
              />
            </v-list-item>
            <v-divider class="my-2"></v-divider>
            <v-list-item>
              <div class="d-flex ga-2" style="width: 100%">
                <v-btn
                  data-testid="filter-check-all-btn"
                  size="small"
                  variant="text"
                  @click="checkAllFilters"
                >
                  Check All
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn
                  data-testid="filter-apply-btn"
                  size="small"
                  color="primary"
                  variant="flat"
                  @click="applyFilters"
                >
                  Apply
                </v-btn>
              </div>
            </v-list-item>
          </v-list>
        </v-menu>

        <v-spacer></v-spacer>
        <!-- Desktop search -->
        <v-text-field
          data-testid="user-search-input"
          v-if="!$vuetify.display.mdAndDown"
          v-model="searchQuery"
          placeholder="Search users…"
          clearable
          prepend-inner-icon="mdi-magnify"
          hide-details
          density="compact"
          variant="solo"
          class="mr-2"
          style="max-width: 380px"
        ></v-text-field>
      </v-toolbar>

      <!-- Mobile search -->
      <v-card-text v-if="$vuetify.display.mdAndDown" class="pb-2">
        <v-text-field
          v-model="searchQuery"
          placeholder="Search users…"
          clearable
          prepend-inner-icon="mdi-magnify"
          hide-details
          density="compact"
          variant="solo"
        ></v-text-field>
      </v-card-text>

      <v-data-table
        data-testid="users-table"
        :headers="headers"
        :items="filteredDisplayedUsers"
        item-value="username"
        :loading="isLoading"
        loading-text="Loading users..."
        class="elevation-1 sticky-header striped-rows even-cols"
        :style="{ '--even-col-count': headers.length }"
        height="600px"
        fixed-header
        style="max-width: 100%; width: 100%; overflow-x: auto"
      >
        <template #item.status="{ item }">
          <v-chip
            size="x-small"
            :color="item?.status === 'Verified' ? 'green' : 'red'"
            variant="tonal"
            class="status-chip"
          >
            {{ item?.status }}
          </v-chip>
        </template>
        <template #item.actions="{ item }">
          <div class="d-flex align-center">
            <!-- 3-dot menu -->
            <v-menu offset-y>
              <template v-slot:activator="{ props }">
                <v-btn
                  icon
                  variant="text"
                  size="x-small"
                  class="subtle-action mr-2"
                  color="grey-darken-1"
                  v-bind="props"
                  @click.stop
                >
                  <v-icon size="20">mdi-dots-horizontal</v-icon>
                </v-btn>
              </template>

              <v-list dense>
                <!-- Update User Role -->
                <v-list-item
                  data-testid="update-role-btn"
                  @click.stop="openRoleDialog(item)"
                  class="list-item-action"
                >
                  <v-icon color="primary" class="mr-2">mdi-account-edit</v-icon>
                  <span>Update User Role</span>
                </v-list-item>

                <!-- Toggle Active/Inactive -->
                <v-list-item
                  data-testid="toggle-status-btn"
                  @click.stop="promptToggle(item)"
                  class="list-item-action"
                >
                  <v-icon :color="item.enabled ? 'success' : 'error'" class="mr-2">
                    {{ item.enabled ? 'mdi-toggle-switch' : 'mdi-toggle-switch-off' }}
                  </v-icon>
                  <span>{{ item.enabled ? 'Deactivate' : 'Activate' }}</span>
                </v-list-item>

                <!-- Delete User -->
                <v-list-item
                  data-testid="delete-user-btn"
                  @click.stop="promptDelete(item.username)"
                  class="list-item-action"
                >
                  <v-icon color="error" class="mr-2">mdi-delete</v-icon>
                  <span>Delete User</span>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" data-testid="delete-dialog" max-width="400px">
      <v-card>
        <v-card-title style="font-weight: 600" class="headline">Confirm Delete</v-card-title>
        <v-card-text>
          Are you sure you want to permanently delete user
          <strong>{{ userToDelete }}</strong>
          ?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn data-testid="delete-dialog-cancel" text @click="deleteDialog = false">
            Cancel
          </v-btn>
          <v-btn data-testid="delete-dialog-confirm" color="red" text @click="confirmDelete">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="toggleDialog" data-testid="toggle-dialog" max-width="400px">
      <v-card>
        <v-card-title class="headline">
          {{ toggleTarget.enabled ? 'Deactivate' : 'Activate' }} "{{ toggleTarget.username }}"?
        </v-card-title>
        <v-card-text>
          Are you sure you want to
          <strong>{{ toggleTarget.enabled ? 'deactivate' : 'activate' }}</strong>
          this account?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn data-testid="toggle-dialog-cancel" text @click="toggleDialog = false">
            Cancel
          </v-btn>
          <v-btn data-testid="toggle-dialog-confirm" color="primary" text @click="confirmToggle">
            {{ toggleTarget.enabled ? 'Deactivate' : 'Activate' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="roleDialog" data-testid="role-dialog" max-width="400px">
      <v-card>
        <v-card-title class="headline">Update Role</v-card-title>
        <v-card-text>
          <div>
            Change role for
            <strong>{{ userToUpdateRole?.username }}</strong>
            , Current role is
            <strong>{{ userToUpdateRole?.role }}</strong>
            &nbsp;
            <br />
            &nbsp;
            <v-select
              v-model="selectedNewRole"
              data-testid="role-select"
              :items="allGroups"
              label="Select new role"
              required
            ></v-select>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn data-testid="role-dialog-cancel" text @click="roleDialog = false">Cancel</v-btn>
          <v-btn
            data-testid="role-dialog-confirm"
            color="primary"
            text
            @click="confirmUpdateRole"
            :loading="updatingUserRole"
          >
            Update
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-snackbar v-model="snackbar" color="info" :timeout="3000">
      {{ message }}
      <template v-slot:action="{ attrs }">
        <v-btn color="white" text v-bind="attrs" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useUserStore } from '@/stores/userStore'
import router from '@/router'
import {
  fetchAllUsers,
  //  fetchAllAdmins,
  deleteUser,
  toggleUserStatusSetActiveNow,
  toggleUserStatusSetDeActiveNow,
  updateRole,
  VerifyUsers,
} from '@/api/accountsClient'

const isLoading = ref(true)
const allUsers = ref([])
const allAdmins = ref([])
const selectedRole = ref('users')
// Search and tabs like ClientUsers.vue
const searchQuery = ref('')
const searchRef = ref()
const tab = ref('all')
// Role checkbox state (default all selected)
const selectedRoles = ref(['user', 'manager', 'admin'])
const tempSelectedRoles = ref(['user', 'manager', 'admin'])
const filterMenuOpen = ref(false)

const deleteDialog = ref(false)
const dialogConfirm = ref(false)
const userToDelete = ref(null)
const message = ref('')
const snackbar = ref(false)

const toggleDialog = ref(false)
const toggleTarget = ref({ username: '', enabled: false })
const updatingUserRole = ref(false)

const roleDialog = ref(false)
const userToUpdateRole = ref(null)
const selectedNewRole = ref('')
const userStore = useUserStore()
const userRole = ref(userStore?.role)

function toTitleWords(text) {
  return String(text)
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

function friendlyRoleFromRaw(raw) {
  if (!raw) return 'N/A'
  const base = String(raw)
    .toLowerCase()
    .replace(/^(client|company)_/, '')
  if (base === 'admin') return 'Admin'
  if (base === 'manager') return 'Manager'
  if (base === 'user') return 'User'
  return toTitleWords(base)
}

function openRoleDialog(user) {
  console.log('Opening role dialog for', user) // ✅ add this
  userToUpdateRole.value = user
  selectedNewRole.value = user?.role?.replace(/\s+/g, '_')
  roleDialog.value = true
}
async function confirmUpdateRole() {
  updatingUserRole.value = true
  try {
    if (!selectedNewRole.value || !userToUpdateRole.value?.username) return

    await updateRole(userToUpdateRole.value.username, selectedNewRole.value, true)
    message.value = '✅ Role updated successfully.'
    snackbar.value = true
    await (selectedRole.value === 'clients' ? loadAllClients() : loadUsers())
    updatingUserRole.value = false
  } catch (e) {
    message.value = '❌ Failed to update role.'
    snackbar.value = true
    updatingUserRole.value = false
  } finally {
    roleDialog.value = false
    updatingUserRole.value = false
  }
}
const allGroups = ['client_user', 'client_manager', 'client_admin']

function formatDate(raw) {
  // First normalize your two formats into a real Date()
  let dt
  if (/^\d{4}-\d{2}-\d{2}T/.test(raw)) {
    dt = new Date(raw)
  } else {
    // handle "YYYY-MM-DD hh:mm AM/PM"
    const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
    if (m) {
      let [, y, M, d, h, mm, ampm] = m
      h = parseInt(h, 10)
      if (ampm.toUpperCase() === 'PM' && h !== 12) h += 12
      if (ampm.toUpperCase() === 'AM' && h === 12) h = 0
      dt = new Date(parseInt(y, 10), parseInt(M, 10) - 1, parseInt(d, 10), h, parseInt(mm, 10))
    } else {
      // fallback
      dt = new Date(raw)
    }
  }

  if (isNaN(dt)) return 'Invalid date'
  // now format however you like—here’s US‐style with full date & time:
  return dt.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
}

const loadUsers = async () => {
  isLoading.value = true
  try {
    const data = await fetchAllUsers()
    allUsers.value = data.map((user) => ({
      username: user.Username,
      email: user.Attributes.find((attr) => attr.Name === 'email')?.Value || 'N/A',
      role: friendlyRoleFromRaw(user?.Attributes.find((a) => a.Name === 'custom:role')?.Value),

      userCreateDate: formatDate(user.UserCreateDate),
      userLastModifiedDate: formatDate(user.UserLastModifiedDate),
      enabled: user?.Enabled,
      status: user?.UserStatus === 'CONFIRMED' ? 'Verified' : 'Unverified',
    }))
  } catch (error) {
    console.error('Failed to load users:', error)
  } finally {
    isLoading.value = false
  }
}

// Computed property to switch between users and admins
const displayedUsers = computed(() =>
  selectedRole.value === 'users' ? allUsers.value : allAdmins.value
)

const totalUsers = computed(() => displayedUsers.value.length)

const roleCounts = computed(() => {
  const counts = { admin: 0, manager: 0, user: 0 }
  for (const u of displayedUsers.value) {
    const key = String(u.customRoleKey || u.role || '')
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/^(client|company)_/, '') // admin|manager|user
    if (counts[key] != null) counts[key]++
  }
  return counts
})

const filteredDisplayedUsers = computed(() => {
  const q = searchQuery.value.trim().toLowerCase()
  const roles = new Set((selectedRoles.value || []).map((r) => String(r).toLowerCase()))
  let list = displayedUsers.value
  // Filter by selected roles
  list = list.filter((u) => {
    const key = String(u.customRoleKey || u.role || '')
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/^(client|company)_/, '')
    return roles.has(key)
  })
  if (!q) return list
  return list.filter(
    (u) =>
      (u.username || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.customRole || u.role || '').toLowerCase().includes(q)
  )
})

onMounted(() => {
  VerifyUsers()
  loadUsers()
})

const headers = [
  { title: 'Username', align: 'start', key: 'username', minWidth: '250px' },
  { title: 'Email', key: 'email' },
  { title: 'Role', key: 'role' },
  { title: 'Status', key: 'status' },

  { title: 'Actions', key: 'actions', align: 'start', minWidth: '120px' },
]

const navigateToCreateUser = () => {
  // Client admin creation: route to client-specific create user view and pass JUID
  const juid = userStore?.juid
  router.push({
    path: '/users/create-user',
    query: juid ? { juid } : undefined,
  })
}

function promptDelete(item) {
  console.log(item)
  userToDelete.value = item
  deleteDialog.value = true
}
async function confirmDelete() {
  console.log(userToDelete.value)
  if (!userToDelete.value) return
  try {
    await deleteUser(userToDelete.value)
    message.value = 'User Deleted Successfully'
    snackbar.value = true
    // refresh the list
    if (selectedRole.value === 'users') await loadUsers()
    else await loadAdmins()
  } catch (err) {
    console.error('Failed to delete', err)
    message.value = 'Error Deleting User'
  } finally {
    deleteDialog.value = false
    userToDelete.value = null
  }
}

function promptToggle(item) {
  toggleTarget.value = { username: item.username, enabled: item.enabled }
  console.log(toggleTarget.value)
  toggleDialog.value = true
}
async function confirmToggle() {
  try {
    if (toggleTarget.value.enabled) {
      // currently enabled → deactivate
      await toggleUserStatusSetDeActiveNow(toggleTarget.value.username)
      message.value = 'User Deactivate Successfully'
      snackbar.value = true
    } else {
      // currently disabled → activate
      await toggleUserStatusSetActiveNow(toggleTarget.value.username)
      message.value = 'User Activate Successfully'
      snackbar.value = true
    }
    // refresh list
    await loadUsers()
  } catch (e) {
    console.error(e)
    message.value = 'Error Toggle User Status'
    snackbar.value = true
  } finally {
    toggleDialog.value = false
  }
}

// Filter functions
function applyFilters() {
  selectedRoles.value = [...tempSelectedRoles.value]
  filterMenuOpen.value = false
}

function checkAllFilters() {
  tempSelectedRoles.value = ['user', 'manager', 'admin']
}
</script>

<style scoped>
/* Add any custom styling here */
::deep(.v-data-table tbody tr:hover) {
  background-color: #f0f0f0 !important;
}

.v-data-table {
  max-width: 100%;
  width: 100%;
  overflow-x: auto;
}
.v-data-table tbody tr:hover {
  background-color: #f0f0f0 !important;
}

::deep(.v-data-table tbody tr:hover) {
  background-color: #f0f0f0 !important;
}
::deep(.v-data-table__tr:hover) {
  background-color: #f0f0f0 !important;
}

.even-cols :deep(.v-data-table__wrapper table) {
  table-layout: fixed;
}

.even-cols :deep(th),
.even-cols :deep(td) {
  width: calc(100% / var(--even-col-count, 5));
}
.custom-button {
  width: 100%;
  height: 57px;
}

@media (max-width: 600px) {
  .custom-button {
    margin-top: 15px;
    width: 100%;
  }
}

.pa4 {
  border: 2px solid red !important;
}

.subheader-sticky {
  position: sticky;
  top: 56px; /* below app bar and account header */
  z-index: 3;
  background: white;
}

@media (max-width: 960px) {
  .subheader-sticky {
    top: 56px;
  }
}

.status-text {
  font-weight: 600;
}
.status-active {
  color: #2e7d32;
}
.status-inactive {
  color: #c62828;
}
</style>
